import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import type {
  ActivityNode,
  ConfigActivityRow,
} from "@/api/config-server-stages";
import type { DraftFilters } from "@/api/management";
import { SscuiActivityModal } from "@/components/agents/configuration-server/SscuiActivityModal";
import {
  ActivityDecidedList,
  ActivityDecisionList,
  type ActivitySelection,
} from "@/components/agents/configuration-server/stages/ActivityGroupedList";
import {
  NO_ACTIVITY_FILTERS,
  type ActivityFiltersState,
} from "@/components/agents/configuration-server/stages/activityFilters";
import { BulkActions } from "@/components/agents/configuration-server/stages/BulkActions";
import {
  useDraftVerdicts,
  useDraftItems,
  useManagementDraft,
} from "@/components/agents/configuration-server/stages/managementDraftContext";
import { TransferLayout } from "@/components/agents/configuration-server/stages/TransferLayout";
import { StageLoading } from "@/components/agents/configuration-server/stages/StageLoading";
import { Button } from "@/components/ui/button";
import { useDebouncedSearch } from "@/hooks/useDebouncedSearch";
import { useLiveAnnouncement } from "@/hooks/useLiveAnnouncement";
import {
  useActivityAreas,
  useActivitiesScroll,
} from "@/hooks/useManagementLists";
import { PANEL_PAGE_SIZE } from "@/lib/config-pagination";
import { LoadMore } from "./Paging";
import { ActivityTree } from "./ActivityTree";
import type { ScopeVerdict } from "@/lib/org-structure-tree";

const ACTIVITY_ITEM_TYPE = "activity";
type Verdict = Exclude<ScopeVerdict, "pending">;
/** "Todos" é uma instrução ao servidor; desmarcar um vira a lista do que está na tela. */
type Selection = { mode: "all" } | { mode: "some"; codes: ReadonlySet<string> };
const NONE: Selection = { mode: "some", codes: new Set() };

export interface NodeCountsView {
  pending: number;
  approved: number;
  out_of_scope: number;
}

/**
 * Aba de SSCUI ou de App Fiori do Gerenciamento: pendentes de 100 em 100 (rolagem),
 * Aprovados e Fora do escopo em páginas de 15, tudo filtrado no servidor
 * (`/management/activities`). As decisões vão para o rascunho.
 */
export function ActivityManagementTab({
  projectId,
  artifactId,
  node,
  counts,
}: {
  projectId: string;
  artifactId: string;
  node: ActivityNode;
  /** Contagens do servidor já com o rascunho. */
  counts: NodeCountsView;
}) {
  const { t } = useTranslation();
  const kind = node === "sscui" ? "sscui" : "fiori";
  const draft = useManagementDraft();
  const draftVerdicts = useDraftVerdicts(node);
  const [filters, setFilters] =
    useState<ActivityFiltersState>(NO_ACTIVITY_FILTERS);
  const [q, commitSearch] = useDebouncedSearch(filters.q);
  const query = useMemo(
    () => ({
      node,
      verdict: "pending" as const,
      q,
      priority: filters.priority,
      country: filters.country,
    }),
    [node, q, filters.priority, filters.country],
  );
  const flat =
    node === "module_config" ||
    !!(q.trim() || filters.priority || filters.country);
  const areas = useActivityAreas(projectId, artifactId, query, !flat);
  const scroll = useActivitiesScroll(projectId, artifactId, query, flat);
  const first = scroll.data?.pages[0];
  const loaded = useMemo(
    () => scroll.data?.pages.flatMap((page) => page.items) ?? [],
    [scroll.data],
  );
  const rows = useMemo(
    () =>
      loaded.filter(
        (row) =>
          (draftVerdicts.get(row.activity_code) ?? "pending") === "pending",
      ),
    [loaded, draftVerdicts],
  );
  const returnedDetails = useDraftItems(node, "pending");
  const returned = returnedDetails.items;
  const filtersActive = !!(q.trim() || filters.priority || filters.country);
  const [selection, setSelection] = useState<Selection>(NONE);
  const [details, setDetails] = useState<ConfigActivityRow | null>(null);
  const [announcement, setAnnouncement] = useLiveAnnouncement();
  const container = useRef<HTMLDivElement>(null);
  const [focusNext, setFocusNext] = useState<string | null | undefined>(
    undefined,
  );

  // Trocar o filtro troca a lista: a seleção começa de novo.
  useEffect(() => {
    setSelection(NONE);
  }, [query]);

  const visibleCodes = useMemo(
    () => [
      ...new Set([
        ...returned.map((item) => item.code),
        ...rows.map((row) => row.activity_code),
      ]),
    ],
    [returned, rows],
  );
  const selectedCount =
    selection.mode === "all"
      ? Math.max(0, filtersActive ? (first?.total_codes ?? 0) : counts.pending)
      : selection.codes.size;
  const someChecked = selection.mode === "some" && selection.codes.size > 0;
  const allChecked = selection.mode === "all";
  const selectionApi: ActivitySelection = {
    isChecked: (code) => selection.mode === "all" || selection.codes.has(code),
    allState: allChecked ? true : someChecked ? "indeterminate" : false,
    onToggleAll: (checked) => setSelection(checked ? { mode: "all" } : NONE),
    onToggle: (codes, checked) =>
      setSelection((current) => {
        // Desmarcar um com "todos" marcado: fica o que está na tela, menos ele.
        const shown = Array.from(
          container.current?.querySelectorAll<HTMLElement>(
            "[data-pending-panel] [data-activity-code]",
          ) ?? [],
        ).map((line) => line.dataset.activityCode!);
        const base = new Set(
          current.mode === "all" ? [...visibleCodes, ...shown] : current.codes,
        );
        for (const code of codes) {
          if (checked) base.add(code);
          else base.delete(code);
        }
        return { mode: "some", codes: base };
      }),
  };

  useEffect(() => {
    if (focusNext === undefined || !container.current) return;
    const lines = Array.from(
      container.current.querySelectorAll<HTMLElement>(
        "[data-pending-panel] [data-activity-code]",
      ),
    );
    const target = lines.find(
      (line) => line.dataset.activityCode === focusNext,
    );
    (
      target?.querySelector<HTMLElement>("[data-pending-action]") ??
      container.current.querySelector<HTMLElement>("[data-pending-panel] h2")
    )?.focus();
    setFocusNext(undefined);
  }, [rows, focusNext]);

  const message = (verdict: ScopeVerdict, count: number) =>
    verdict === "approved"
      ? t("{{count}} items approved", { count })
      : verdict === "out_of_scope"
        ? t("{{count}} items taken out of scope", { count })
        : t("{{count}} items returned to pending", { count });

  const decide = (codes: string[], verdict: ScopeVerdict) => {
    const order = visibleCodes;
    const index = order.findIndex((code) => codes.includes(code));
    const next = [...order.slice(index + 1), ...order.slice(0, index + 1)].find(
      (code) => !codes.includes(code),
    );
    draft?.stage({
      node,
      verdict,
      items: codes.map((code) => ({
        item_type: ACTIVITY_ITEM_TYPE,
        item_key: code,
      })),
    });
    setAnnouncement(message(verdict, codes.length));
    if (verdict !== "pending") setFocusNext(next ?? null);
  };
  const decideSelected = (verdict: Verdict) => {
    if (selection.mode === "all") {
      const all_pending: DraftFilters = {
        q: q || undefined,
        priority: filters.priority || undefined,
        country: filters.country || undefined,
      };
      draft?.stage({ node, verdict, items: [], all_pending });
      setAnnouncement(message(verdict, selectedCount));
    } else {
      decide([...selection.codes], verdict);
    }
    setSelection(NONE);
  };

  const pendingQuery = flat ? scroll : areas;
  const pendingList =
    pendingQuery.isError && !pendingQuery.data ? (
      <div role="alert" className="p-5">
        <p>{t("Could not load configurations.")}</p>
        <Button onClick={() => void pendingQuery.refetch()}>
          {t("Retry")}
        </Button>
      </div>
    ) : pendingQuery.isPending ? (
      <div className="p-5">
        <StageLoading variant="list" />
      </div>
    ) : (
      <ActivityDecisionList
        kind={kind}
        onSearchEnter={commitSearch}
        rows={rows}
        unsaved={returned}
        unsavedFooter={
          returnedDetails.hasMore ||
          returnedDetails.loading ||
          returnedDetails.error ? (
            <DraftDetailsMore details={returnedDetails} />
          ) : undefined
        }
        filters={filters}
        appliedQ={q}
        onFiltersChange={setFilters}
        countries={
          flat ? (first?.countries ?? []) : (areas.data?.countries ?? [])
        }
        totals={{
          areas: flat
            ? (first?.area_count ?? 0)
            : (areas.data?.items.length ?? 0),
          codes: flat
            ? (first?.total_codes ?? 0)
            : (areas.data?.total_codes ?? 0),
        }}
        tree={
          !flat && !!areas.data?.items.length ? (
            <ActivityTree
              projectId={projectId}
              artifactId={artifactId}
              query={query}
              areas={areas.data?.items ?? []}
              selection={selectionApi}
              onVerdict={decide}
              onDetails={setDetails}
            />
          ) : undefined
        }
        selection={selectionApi}
        onVerdict={decide}
        onDetails={setDetails}
        pagingError={
          scroll.isError && scroll.data ? (
            <div role="alert" className="p-3">
              <Button onClick={() => void scroll.fetchNextPage()}>
                {t("Retry")}
              </Button>
            </div>
          ) : undefined
        }
        hasMore={!!scroll.hasNextPage}
        loadingMore={scroll.isFetchingNextPage}
        onLoadMore={() => void scroll.fetchNextPage()}
      />
    );

  return (
    <div ref={container}>
      <p
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {announcement.message && (
          <span key={announcement.sequence}>{announcement.message}</span>
        )}
      </p>
      <TransferLayout
        decidedOnly={
          counts.pending === 0 && returned.length === 0 && !filtersActive
        }
        pendingTitle={t("Pending")}
        pendingCount={counts.pending}
        pendingActions={
          <BulkActions
            count={selectedCount}
            onApprove={() => decideSelected("approved")}
            onOutOfScope={() => decideSelected("out_of_scope")}
            onClear={() => setSelection(NONE)}
          />
        }
        pending={pendingList}
        approvedCount={counts.approved}
        approved={
          <DecidedPanel
            projectId={projectId}
            artifactId={artifactId}
            node={node}
            verdict="approved"
            onReturn={(code) => decide([code], "pending")}
            onDetails={setDetails}
          />
        }
        outOfScopeCount={counts.out_of_scope}
        outOfScope={
          <DecidedPanel
            projectId={projectId}
            artifactId={artifactId}
            node={node}
            verdict="out_of_scope"
            onReturn={(code) => decide([code], "pending")}
            onDetails={setDetails}
          />
        }
      />
      {details && (
        <SscuiActivityModal
          open
          onClose={() => setDetails(null)}
          projectId={projectId}
          activityCode={details.activity_code}
          artifactId={artifactId}
          node={node}
          meta={{
            name: details.name ?? null,
            priority: details.priority ?? null,
            countries: details.countries ?? [],
            subarea: details.application_subarea ?? null,
            scopeItems: details.replicated_in || undefined,
          }}
        />
      )}
    </div>
  );
}

function DecidedPanel({
  projectId,
  artifactId,
  node,
  verdict,
  onReturn,
  onDetails,
}: {
  projectId: string;
  artifactId: string;
  node: ActivityNode;
  verdict: Verdict;
  onReturn: (code: string) => void;
  onDetails: (row: ConfigActivityRow) => void;
}) {
  const { t } = useTranslation();
  const draftDetails = useDraftItems(node, verdict);
  const draftVerdicts = useDraftVerdicts(node);
  const [search, setSearch] = useState("");
  const [q, commitSearch] = useDebouncedSearch(search);
  const query = { node, verdict, q };
  const flat = node === "module_config" || !!q.trim();
  const areas = useActivityAreas(projectId, artifactId, query, !flat);
  const result = useActivitiesScroll(
    projectId,
    artifactId,
    query,
    flat,
    PANEL_PAGE_SIZE,
  );
  const rows = (result.data?.pages.flatMap((page) => page.items) ?? []).filter(
    (row) => (draftVerdicts.get(row.activity_code) ?? verdict) === verdict,
  );
  const unsaved = draftDetails.items.filter(
    (item) =>
      !q ||
      `${item.code} ${item.name ?? ""}`
        .toLocaleLowerCase()
        .includes(q.toLocaleLowerCase()),
  );
  const activeQuery = flat ? result : areas;
  if (activeQuery.isError && !activeQuery.data)
    return (
      <div role="alert" className="p-4">
        <p>{t("Could not load configurations.")}</p>
        <Button onClick={() => void activeQuery.refetch()}>{t("Retry")}</Button>
      </div>
    );
  if (activeQuery.isPending) return <StageLoading variant="panel" />;
  return (
    <ActivityDecidedList
      kind={node === "sscui" ? "sscui" : "fiori"}
      rows={rows}
      unsaved={unsaved}
      unsavedFooter={
        draftDetails.hasMore || draftDetails.loading || draftDetails.error ? (
          <DraftDetailsMore details={draftDetails} />
        ) : undefined
      }
      search={search}
      appliedSearch={q}
      onSearch={setSearch}
      onSearchEnter={commitSearch}
      searchPlaceholder={t(
        verdict === "approved" ? "Search approved" : "Search out of scope",
      )}
      total={
        flat
          ? (result.data?.pages[0]?.total_codes ?? 0)
          : (areas.data?.total_codes ?? 0)
      }
      tree={
        !flat && !!areas.data?.items.length ? (
          <ActivityTree
            projectId={projectId}
            artifactId={artifactId}
            query={query}
            areas={areas.data?.items ?? []}
            limit={PANEL_PAGE_SIZE}
            onReturn={onReturn}
            onDetails={onDetails}
          />
        ) : undefined
      }
      pagingError={
        result.isError && result.data ? (
          <div role="alert" className="p-3">
            <Button onClick={() => void result.fetchNextPage()}>
              {t("Retry")}
            </Button>
          </div>
        ) : undefined
      }
      hasMore={!!result.hasNextPage}
      loadingMore={result.isFetchingNextPage}
      onLoadMore={() => void result.fetchNextPage()}
      onReturn={onReturn}
      onDetails={onDetails}
    />
  );
}

export function DraftDetailsMore({
  details,
}: {
  details: ReturnType<typeof useDraftItems>;
}) {
  const { t } = useTranslation();
  return details.error ? (
    <div role="alert" className="p-3">
      <button type="button" onClick={details.retry}>
        {t("Retry")}
      </button>
    </div>
  ) : (
    <LoadMore
      hasMore={details.hasMore}
      loading={details.loading}
      onLoadMore={details.loadMore}
    />
  );
}
